import { ComponentProps, useCallback, useState } from 'react'
import { MEDIA_FALLBACK_URL } from '~constants'
import { cn } from '~utils/cn'
import resourceUriDecorate, { RequiredWidth } from '~utils/resource-uri-decorator'

import Icon from '~components/ui/icon'
import Spinner from '~components/ui/loading-indicator/spinner'

export type MediaSize = '48' | '64' | '96'
type MediaSizeCss = { [key in MediaSize]: string }

const MEDIA_SIZE_CSS: MediaSizeCss = {
  '48': 'size-12',
  '64': 'size-16',
  '96': 'size-24',
}

type MediaProps = Readonly<
  ComponentProps<'figure'> & {
    url: string
    alt: string
    size?: MediaSize
    shape?: 'square' | 'round'
    withSpinner?: boolean
    onClick?: () => void
  }
>

enum LoadingState {
  Loading,
  Loaded,
  Error,
}

/**
 * video url은 thumbnail_url query string이 포함되어 내려옴.
 */
export const VIDEO_TYPE_DIVIDER = '?thumbnail_url='
export const getMediaType = (url: string) => (url.includes(VIDEO_TYPE_DIVIDER) ? 'video' : 'photo')
export const getVideoInfo = (url: string) => {
  const dividedUrl = url.split(VIDEO_TYPE_DIVIDER)
  const videoUrl = dividedUrl.length > 1 ? dividedUrl[0] : ''
  const videoThumbnailUrl = dividedUrl.length > 1 ? dividedUrl[1] : MEDIA_FALLBACK_URL

  return { videoUrl, videoThumbnailUrl }
}

/**
 * @version v1.1.2
 * @author Danny
 * @description
 * - Image 컴포넌트에서 deprecated된 onLoadingComplete를 onLoad prop으로 대체
 * - LoadingState enum 적용 - load error가 발생했음에도 spinner가 도는 문제가 있었음
 */
const Media = ({
  url,
  alt,
  size = '96',
  shape = 'round',
  withSpinner = true,
  className,
  onClick,
}: MediaProps) => {
  const [loadingState, setLoadState] = useState(LoadingState.Loading)
  const type = getMediaType(url)
  const _size = parseInt(size, 10)
  const _url = type === 'video' ? getVideoInfo(url).videoThumbnailUrl : url

  const handleLoadingState = useCallback(
    (state: LoadingState) => () => {
      setLoadState(state)
    },
    []
  )

  return (
    <figure
      role="presentation"
      className={cn('relative h-fit w-fit flex-shrink-0 cursor-pointer', className)}
      onClick={onClick}
    >
      <div
        className={cn(
          'overflow-hidden border border-solid',
          shape === 'round' && 'rounded-lg border-gray-200 bg-gray-300',
          MEDIA_SIZE_CSS[size]
        )}
      >
        <img
          src={resourceUriDecorate(_url, {
            width: RequiredWidth.W150,
          })}
          width={_size}
          height={_size}
          alt={alt}
          className={cn('h-full w-full', type === 'video' && 'brightness-[40%]')}
          style={{
            objectFit: 'cover',
          }}
          onLoad={handleLoadingState(LoadingState.Loaded)}
          onError={handleLoadingState(LoadingState.Error)}
        />
      </div>
      {type === 'video' && (
        <Icon
          name="play"
          className="absolute left-1/2 top-1/2 size-10 -translate-x-1/2 -translate-y-1/2 fill-white"
        />
      )}
      {withSpinner && loadingState === LoadingState.Loading && (
        <div className="absolute left-1/2 top-1/2 z-floated -translate-x-1/2 -translate-y-1/2">
          <Spinner color="gray" />
        </div>
      )}
    </figure>
  )
}

export default Media
